/**
 * SignaturePad — full-screen-friendly canvas where the user draws their
 * signature with a finger (touch) or mouse. Designed primarily for mobile.
 *
 * Props:
 *   - onCancel(): void
 *   - onSubmit(b64Png: string): Promise<void> | void   // base64 (data URI accepted)
 *   - title?: string
 *   - subtitle?: string
 *
 * The component exposes the standard HTML5 Canvas events and supports both
 * Pointer Events (mouse + touch + stylus) and a Touch-Events fallback for
 * older mobile browsers.
 *
 * The drawing is exported as PNG via `canvas.toDataURL('image/png')` after
 * being trimmed of fully-transparent margins (white background is replaced
 * by transparency so the embedded signature blends with the document).
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Eraser, X, Check, Loader2 } from 'lucide-react';

const PEN_COLOR = '#0f172a';
const PEN_WIDTH = 2.8;

export default function SignaturePad({ onCancel, onSubmit, title = 'Firma qui', subtitle = 'Usa il dito o lo stilo per firmare nel riquadro sottostante.' }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const lastPoint = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Setup canvas dimensions + DPR. Re-runs on window resize.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const setup = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = PEN_COLOR;
      ctx.lineWidth = PEN_WIDTH;
      // Background: white (we'll convert white → transparent at export time).
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, rect.width, rect.height);
    };
    setup();
    window.addEventListener('resize', setup);
    return () => window.removeEventListener('resize', setup);
  }, []);

  const getPoint = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches?.[0] || e.changedTouches?.[0];
    const clientX = touch ? touch.clientX : e.clientX;
    const clientY = touch ? touch.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const startDraw = useCallback((e) => {
    e.preventDefault();
    drawing.current = true;
    hasInk.current = true;
    setIsEmpty(false);
    lastPoint.current = getPoint(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
  }, [getPoint]);

  const moveDraw = useCallback((e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = getPoint(e);
    const ctx = canvasRef.current.getContext('2d');
    // Smooth via mid-point quadratic curve.
    const mid = { x: (lastPoint.current.x + p.x) / 2, y: (lastPoint.current.y + p.y) / 2 };
    ctx.quadraticCurveTo(lastPoint.current.x, lastPoint.current.y, mid.x, mid.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(mid.x, mid.y);
    lastPoint.current = p;
  }, [getPoint]);

  const endDraw = useCallback(() => {
    if (!drawing.current) return;
    drawing.current = false;
    const ctx = canvasRef.current.getContext('2d');
    ctx.closePath();
  }, []);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    hasInk.current = false;
    setIsEmpty(true);
  }, []);

  const exportPng = useCallback(() => {
    // Convert white pixels → transparent so signature embeds cleanly on PDFs.
    const canvas = canvasRef.current;
    const tmp = document.createElement('canvas');
    tmp.width = canvas.width;
    tmp.height = canvas.height;
    const tctx = tmp.getContext('2d');
    tctx.drawImage(canvas, 0, 0);
    const imgData = tctx.getImageData(0, 0, tmp.width, tmp.height);
    const d = imgData.data;
    for (let i = 0; i < d.length; i += 4) {
      // If pixel is "near white", make it transparent.
      if (d[i] > 240 && d[i + 1] > 240 && d[i + 2] > 240) {
        d[i + 3] = 0;
      }
    }
    tctx.putImageData(imgData, 0, 0);
    return tmp.toDataURL('image/png');
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isEmpty || submitting) return;
    setSubmitting(true);
    try {
      const b64 = exportPng();
      await onSubmit?.(b64);
    } finally {
      setSubmitting(false);
    }
  }, [exportPng, isEmpty, onSubmit, submitting]);

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/85 backdrop-blur-sm flex flex-col p-4 md:p-8 pointer-events-auto" data-testid="signature-pad-overlay">
      <div className="flex items-start justify-between mb-3 text-white">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-300">Firma documento</p>
          <h2 className="text-xl md:text-2xl font-semibold mt-1">{title}</h2>
          <p className="text-sm text-slate-300 mt-1 max-w-lg">{subtitle}</p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="p-2 rounded-full hover:bg-white/10"
          aria-label="Chiudi"
          data-testid="signature-close"
        ><X className="w-5 h-5" /></button>
      </div>

      <div className="flex-1 bg-white rounded-md shadow-2xl relative overflow-hidden">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full touch-none"
          onPointerDown={startDraw}
          onPointerMove={moveDraw}
          onPointerUp={endDraw}
          onPointerCancel={endDraw}
          onPointerLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={moveDraw}
          onTouchEnd={endDraw}
          onMouseDown={startDraw}
          onMouseMove={moveDraw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          data-testid="signature-canvas"
        />
        {/* Dashed baseline as a visual guide. */}
        <div className="absolute left-8 right-8 bottom-12 border-t border-dashed border-slate-300 pointer-events-none">
          <span className="absolute -bottom-5 left-0 text-[10px] uppercase tracking-wider text-slate-400">Firma qui sopra</span>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 justify-end" data-testid="signature-actions">
        <Button
          type="button"
          variant="outline"
          onClick={clear}
          disabled={isEmpty || submitting}
          className="bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white"
          data-testid="signature-clear"
        ><Eraser className="w-4 h-4 mr-1.5" /> Pulisci</Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={submitting}
          className="bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white"
          data-testid="signature-cancel"
        >Annulla</Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isEmpty || submitting}
          className="bg-emerald-500 hover:bg-emerald-600 text-white min-w-[140px]"
          data-testid="signature-submit"
        >
          {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Invio…</> : <><Check className="w-4 h-4 mr-1.5" /> Conferma firma</>}
        </Button>
      </div>
    </div>
  );
}
